import { InspectionItem, PointArray, PolylineProps } from '@editor/interfaces/interfaces'
import { PayloadAction, createSlice } from '@reduxjs/toolkit'

export interface CustomMeasurement extends Partial<InspectionItem> {
  distanceLabel: Omit<PolylineProps, 'label' | 'segmentLabels'>
}

export interface CustomMeasurementState {
  /**
   * Distance measurements.
   */
  measurements: CustomMeasurement[]

  /**
   * Temporary distance measurements for users without permission to save.
   */
  viewerMeasurements: CustomMeasurement[]
}

const initialState: CustomMeasurementState = {
  measurements: [],
  viewerMeasurements: [],
}

export const slice = createSlice({
  name: 'editor/tools/custom_measurement',
  initialState,
  reducers: {
    /**
     * -- Saved measurements.
     */
    addCustomMeasurement: (state, action: PayloadAction<CustomMeasurement>) => {
      state.measurements.push(action.payload)
    },
    completeCustomMeasurement: (state, { payload }: PayloadAction<PointArray>) => {
      const lastIndex = state.measurements.length - 1
      if (lastIndex < 0) return

      state.measurements[lastIndex].distanceLabel.points.push(payload)
    },
    // Initialize measurements from inspection items.
    // This will only add measurements, not update existing ones.
    initCustomMeasurements: (state, action: PayloadAction<CustomMeasurement[]>) => {
      // add only new measurements
      action.payload.forEach((newMeasurement) => {
        const exists = state.measurements.findIndex(
          (existing) => existing.distanceLabel.id === newMeasurement.distanceLabel.id,
        )

        // if exists, update everything except distanceLabel
        if (exists >= 0) {
          state.measurements[exists] = {
            ...state.measurements[exists],
            ...newMeasurement,
            distanceLabel: state.measurements[exists]?.distanceLabel,
          }
        } else {
          state.measurements.push(newMeasurement)
        }
      })

      // remove measurements that are not in the new list but keep unsaved ones
      state.measurements = state.measurements.filter((existing) =>
        action.payload.some(
          (newMeasurement) =>
            newMeasurement.distanceLabel.id === existing.distanceLabel.id || !existing.inspection_item_id,
        ),
      )
    },
    // Update existing measurements
    updateCustomMeasurements: (state, action: PayloadAction<CustomMeasurement[]>) => {
      state.measurements = state.measurements.map((measurement) => {
        const updatedMeasurement = action.payload.find((item) => item.distanceLabel.id === measurement.distanceLabel.id)
        return updatedMeasurement ? { ...measurement, ...updatedMeasurement } : measurement
      })
    },
    // Clear measurements by ids
    deleteCustomMeasurements: (state, action: PayloadAction<string[]>) => {
      state.measurements = state.measurements.filter(
        (measurement) => !action.payload.includes(measurement.distanceLabel.id),
      )
    },

    /**
     * -- Temporary measurements, for invited users without permission to save.
     */
    addViewerMeasurement: (state, action: PayloadAction<CustomMeasurement>) => {
      state.viewerMeasurements.push(action.payload)
    },
    setViewerMeasurements: (state, action: PayloadAction<CustomMeasurement[]>) => {
      state.viewerMeasurements = action.payload
    },
    completeViewerMeasurement: (state, { payload }: PayloadAction<PointArray>) => {
      const lastIndex = state.viewerMeasurements.length - 1
      if (lastIndex < 0) return

      state.viewerMeasurements[lastIndex].distanceLabel.points.push(payload)
    },
    updateViewerMeasurements: (state, action: PayloadAction<CustomMeasurement[]>) => {
      state.viewerMeasurements = state.viewerMeasurements.map((measurement) => {
        const updatedMeasurement = action.payload.find((item) => item.distanceLabel.id === measurement.distanceLabel.id)
        return updatedMeasurement ? { ...measurement, ...updatedMeasurement } : measurement
      })
    },
    deleteViewerMeasurement: (state, action: PayloadAction<string>) => {
      state.viewerMeasurements = state.viewerMeasurements.filter(
        (measurement) => measurement.distanceLabel.id !== action.payload,
      )
    },

    /**
     * -- Resets
     */
    // Clear unfinished measurements (it will only have 1 point)
    clearUnfinishedMeasurements: (state) => {
      state.measurements = state.measurements.filter((measurement) => measurement.distanceLabel.points.length > 1)
      state.viewerMeasurements = state.viewerMeasurements.filter(
        (measurement) => measurement.distanceLabel.points.length > 1,
      )
    },
  },
})

export const {
  // Functions for users with permission to save
  addCustomMeasurement,
  completeCustomMeasurement,
  initCustomMeasurements,
  updateCustomMeasurements,
  deleteCustomMeasurements,

  // Functions for invited users without permission to save
  addViewerMeasurement,
  completeViewerMeasurement,
  setViewerMeasurements,
  updateViewerMeasurements,
  deleteViewerMeasurement,

  // Resets
  clearUnfinishedMeasurements,
} = slice.actions

export default slice.reducer
